import type { ContentRefKind } from '@gitbook/app-content-refs';
import { canInsertInlineIn, INLINE_MENTION } from '@gitbook/doc-core';
import type { PaletteDef } from '@gitbook/react-uikit';
import type { TextRange } from '@gitbook/slate';
import type { InlineProps, MarkProps, Plugin } from '@gitbook/slate-react';

import type { CreateInputContentRefPaletteOptions } from '../../ContentReferences';
import { createTypePlugin } from '../type-plugin';
import { InlineMention } from './InlineMention';
import { MentionPicker } from './MentionPicker';
import { ALLOWED_CONTENT_REFS_IN_INLINE_MENTION, MENTION_CONTEXT_TYPE } from './util';

type MentionTriggerConfig =
    | ContentRefKind[]
    | {
          getPalette: (input: {
              editor: MarkProps['editor'];
              markData: { symbol: string; value: string };
              getCurrentRange: () => TextRange;
          }) => PaletteDef;
      };

/**
 * Slate plugin to detect user typing "@" or "#".
 */
export const mentionPlugin: Plugin = createMentionPlugin({
    accept: {
        '@': ALLOWED_CONTENT_REFS_IN_INLINE_MENTION,
        '#': ALLOWED_CONTENT_REFS_IN_INLINE_MENTION,
    },
});

export function createMentionPlugin(
    options: Omit<CreateInputContentRefPaletteOptions, 'accept' | 'search'> & {
        accept: {
            [symbol: string]: MentionTriggerConfig;
        };
    }
): Plugin {
    const { accept: _accept, ...restOptions } = options;
    const typePlugin = createTypePlugin({
        markType: MENTION_CONTEXT_TYPE,
        symbols: Object.keys(options.accept),
        getData: ({ value, symbol, textValue }) => {
            const { document, startBlock } = value;
            if (!startBlock) {
                return null;
            }
            if (!canInsertInlineIn({ document, block: startBlock }, INLINE_MENTION)) {
                return null;
            }
            return { symbol, value: textValue };
        },
        renderMark: (props) => {
            const { mark } = props;
            const symbol = mark.data.get('symbol');
            if (typeof symbol !== 'string') {
                return null;
            }

            const acceptForSymbol = options.accept[symbol];

            if (!acceptForSymbol) {
                return null;
            }

            if (!Array.isArray(acceptForSymbol)) {
                return (
                    <MentionPicker
                        {...props}
                        getPalette={(markData, getCurrentRange) =>
                            acceptForSymbol.getPalette({
                                editor: props.editor,
                                markData,
                                getCurrentRange,
                            })
                        }
                    />
                );
            }
            return (
                <MentionPicker
                    {...props}
                    options={{
                        ...restOptions,
                        accept: acceptForSymbol,
                    }}
                />
            );
        },
    });

    const renderNode = (props: InlineProps) => {
        const { node } = props;

        switch (node.type) {
            case INLINE_MENTION:
                return <InlineMention {...props} />;
        }
    };

    return {
        ...typePlugin,
        renderNode,
    };
}
